import {
  Component,
  computed,
  effect,
  linkedSignal,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule, NgForm, NgModel } from '@angular/forms';
import { Store } from '@ngrx/store';
import { NgScrollbar } from 'ngx-scrollbar';
import { AsyncPipe, CommonModule } from '@angular/common';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import dayjs from 'dayjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '../../../../services/notifications.service';
import { CurrentUserService } from '../../../../services/current-user.service';
import { AppState } from '../../../../store/app-state';
import { commitmentsSelectors } from '../../../../store/commitments/commitments.selectors';
import { commitmentsActions } from '../../../../store/commitments/commitments.actions';
import { DeepCopyService } from '../../../../services/deep-copy.service';
import { CompanyDropdownComponent } from '../company-dropdown/company-dropdown.component';
import { InteractionBarStateService } from '../../../../services/interaction-bar-state.service';
import { CommitmentSidebarComponent } from '../commitment-sidebar/commitment-sidebar.component';
import { SpendEntryComponent } from '../spend-entry/spend-entry.component';
import { COMMITMENTS_INTERACTION_BAR_TYPE } from '../../../constants/interaction-bar.constants';
import { fadeInGeneral } from '../../../../../assets/styles/animations';
import {
  COMMITMENT_APPROVAL_STATUS,
  ISidebarContract,
} from '../../../../store/commitments/commitments.types';
import { StorageService } from '../../../../services/storage.service';
import { COMMITMENT_STATUS } from '../../commitments-interaction-bar-view/commitments-interaction-bar-view.utilities';
import { CommitmentSidebarTitleComponent } from '../commitment-sidebar-title/commitment-sidebar-title.component';
import { UploadCommitmentsSidebarComponent } from '../upload-commitments-sidebar/upload-commitments-sidebar.component';
import { OverlayGeneralComponent } from '../../../overlays/overlay-general/overlay-general.component';
import { InputCalendarComponent } from '../../../inputs/input-calendar/input-calendar.component';

import { CommitmentEntryTextComponent } from '../commitment-entry-text/commitment-entry-text.component';
import { MoneyPipe } from '../../../../pipes/framework/money-short.pipe';
import { FloatingInputComponent } from '../../../inputs/floating-input/floating-input.component';
import { DropdownComponent } from '../../../inputs/dropdown/dropdown.component';
import { FloatingTextareaComponent } from '../../../inputs/floating-textarea/floating-textarea.component';
import { SimpleButtonComponent } from '../../../buttons/simple-medium-button/simple-button.component';
import { LoadingSpinnerComponent } from '@app/framework/loading-animation/loading-spinner.component';
import { templateSelectors } from '@app/store/templates/templates.selectors';
import { viewProjectSelectors } from '@app/store/view-project/view-project.selectors';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import {
  COMPANY_DROPDOWN_SELECT_TYPES,
  ICompanyDropdownSelectEvent,
} from '@app/types/company-dropdown.types';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';

@Component({
  selector: 'app-prime-contract-sidebar-mgr',
  templateUrl: './prime-contract-sidebar-mgr.component.html',
  styleUrls: [
    './prime-contract-sidebar-mgr.component.scss',
    '../commitment-sidebar/commitment-sidebar.component.scss',
  ],
  animations: [fadeInGeneral],
  imports: [
    CommitmentSidebarTitleComponent,
    NgScrollbar,
    AsyncPipe,
    FormsModule,
    UploadCommitmentsSidebarComponent,
    CdkOverlayOrigin,
    CommonModule,
    CompanyDropdownComponent,
    OverlayGeneralComponent,
    InputCalendarComponent,
    SpendEntryComponent,
    CommitmentEntryTextComponent,
    MoneyPipe,
    FloatingInputComponent,
    DropdownComponent,
    FloatingTextareaComponent,
    SimpleButtonComponent,
    LoadingSpinnerComponent,
    TooltipDirective,
    SidebarHeaderComponent,
  ],
})
export class PrimeContractSidebarMgrComponent
  extends CommitmentSidebarComponent
  implements OnDestroy
{
  protected readonly COMMITMENT_APPROVAL_STATUS = COMMITMENT_APPROVAL_STATUS;

  @ViewChild('firstPageForm') formFirstPage: NgForm;
  @ViewChildren('spendEntries') spendEntryComponents: QueryList<SpendEntryComponent>;
  @ViewChild('picker') picker;

  private readonly spendEntries = toSignal(
    this.store.select(commitmentsSelectors.selectContractSpendEntries),
  );
  private readonly budgetTemplates = toSignal(
    this.store.select(templateSelectors.getBudgetTagTemplates),
  );
  private readonly lineItems = toSignal(
    this.store.select(viewProjectSelectors.selectLineItemsSummary),
  );

  isCompanyInput = false;
  spendEntriesData = linkedSignal(() => {
    return DeepCopyService.deepCopy(this.spendEntries() || []);
  });
  contract = toSignal(this.store.select(commitmentsSelectors.selectSideBarContract));
  totalContractCost$ = this.store.select(commitmentsSelectors.totalContractCostDescription);

  // this is the mathematical upper limit of how many spend entries can be added
  spendEntriesUpperLimitReached = computed(() => {
    const tags = this.budgetTemplates().reduce(
      (acc, template) => acc + (template.tags.length ?? 0),
      0,
    );
    return this.spendEntries().length >= this.lineItems().length * (tags + 1);
  });

  primeContractSOVs = toSignal(this.store.select(commitmentsSelectors.selectSideBarContractSOVs));

  // cannot add new spend entries or descriptions if there is no more description options left
  noSelectableDescriptionOptions = computed(() => {
    const allOptions = new Set<string>(this.primeContractSOVs().map((sov) => sov.sov_id));
    const costsAdded = this.spendEntriesData().reduce(
      (acc, entry) => acc + (entry?.costs?.length ?? 0),
      0,
    );

    return costsAdded >= allOptions.size;
  });

  disabledAddDescriptionTooltip = computed(() => {
    if (this.noSelectableDescriptionOptions()) {
      return 'There are no more descriptions to select';
    }
    // TODO: check if 'Select a line item first' applies here.
    return null;
  });

  selectedSpendOptions = computed(() => {
    const selectedOptions = new Set<string>();
    const spendEntries = this.spendEntriesData();
    spendEntries.forEach((entry) => {
      entry.costs.forEach((cost) => {
        if (cost.sov_id) selectedOptions.add(cost.sov_id);
      });
    });

    return selectedOptions;
  });

  addSpendEntryDisabled = computed(() => {
    return this.spendEntriesUpperLimitReached() || this.noSelectableDescriptionOptions();
  });

  addSpendEntryTooltip = computed(() => {
    if (this.addSpendEntryDisabled()) {
      return this.noSelectableDescriptionOptions()
        ? 'Cannot add more spend entries since there are no more available SOVs to select'
        : null;
    }
    return null;
  });

  isDropdownShown = {
    company_name: false,
    contract_id: false,
    approval_status: false,
    service_provider: false,
    paid_status: false,
  };
  model: Partial<ISidebarContract> = {
    service_provider: {
      name: '',
      temporary_service_provider_id: null,
      service_provider_user_id: null,
    },
  };

  commitmentType = COMMITMENTS_INTERACTION_BAR_TYPE.PC_TO_CONTRACT;

  firstSpendEntryDate = linkedSignal<string | null>(() => {
    const entriesWithStartDate = this.spendEntriesData().filter((entry) => entry.start_date);
    if (!entriesWithStartDate.length) {
      return null;
    }

    const firstEntry = dayjs.min(entriesWithStartDate.map((entry) => dayjs(entry.start_date)));
    return firstEntry?.format('YYYY-MM-DD') ?? null;
  });

  constructor(
    protected store: Store<AppState>,
    protected notif: NotificationsService,
    protected storage: StorageService,
    protected user: CurrentUserService,
    protected interactionBar: InteractionBarStateService,
  ) {
    super(store, notif, storage, user, interactionBar);

    effect(() => {
      const contract = this.contract();
      if (contract) {
        this.model = DeepCopyService.deepCopy(contract);
        this.isEdit = !!contract.id;
      }
    });
  }

  setFirstSpendEntryDate() {}

  isContractDateDisabled(): boolean {
    if (!this.firstSpendEntryDate || !this.projectData?.start_date) {
      return false;
    }
    const daysDifference = dayjs(this.firstSpendEntryDate()).diff(
      dayjs(this.projectData.start_date),
      'day',
    );
    return daysDifference <= 1;
  }

  selectedCompany(event: ICompanyDropdownSelectEvent, serviceProviderModel: NgModel) {
    switch (event.type) {
      case COMPANY_DROPDOWN_SELECT_TYPES.CHANGE_DROPDOWN: {
        // if new temporary SP is added
        serviceProviderModel.reset();
        this.model.service_provider.service_provider_user_id = null; // we don't link to any SP
        this.model.service_provider.temporary_service_provider_id = null; // we don't link to any temporary SP
        this.isCompanyInput = true;
        break;
      }
      case COMPANY_DROPDOWN_SELECT_TYPES.SELECT: {
        // if user selected an already existing SP (either temporary or not)
        this.model.service_provider = {
          name: event.data.company_name,
        };
        if (event.isTemporaryContractor) {
          this.model.service_provider.temporary_service_provider_id = event.data.id;
        } else {
          this.model.service_provider.service_provider_user_id = event.data.id;
        }
        this.updateContract();
        break;
      }
      default: {
        console.warn('Action not handled', event);
        break;
      }
    }
    this.isDropdownShown.service_provider = false;
  }

  revertToDropdown(event: MouseEvent, serviceProviderUserId: NgModel) {
    this.isCompanyInput = false;
    serviceProviderUserId.reset();
    event.stopPropagation();
  }

  getApprovalStatuses(): COMMITMENT_STATUS[] {
    return [COMMITMENT_STATUS.PENDING, COMMITMENT_STATUS.APPROVED];
  }

  addSpendEntry() {
    this.store.dispatch(commitmentsActions.addContractSpendEntry());
  }

  updateContractDate(date: string) {
    this.store.dispatch(
      commitmentsActions.updateSelectedContract({
        contract: {
          ...this.model,
          added_at: date,
        },
      }),
    );
  }

  updateContract() {
    this.store.dispatch(
      commitmentsActions.updateSelectedContract({ contract: DeepCopyService.deepCopy(this.model) }),
    );
  }

  saveOrModify() {
    if (this.model.id) {
      this.notif.showLoading('Updating contract...');
      this.store.dispatch(commitmentsActions.modifyContract());
      return;
    }
    this.notif.showLoading('Saving contract...');
    this.store.dispatch(commitmentsActions.saveContract());
  }

  nextPage() {
    if (this.spendEntriesData().length === 0) {
      this.addSpendEntry();
    }

    this.incrementPage();
  }
}
